import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useScrollReveal } from "@/hooks/useScrollReveal";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  hero_image: string;
  category: string;
  read_time: string;
}

interface RelatedBlogPostsProps {
  category: string;
  title?: string;
  description?: string;
}

export const RelatedBlogPosts = ({ 
  category, 
  title = "Related Insights",
  description = "Explore our latest thinking"
}: RelatedBlogPostsProps) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const blogReveal = useScrollReveal();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        console.log('Fetching blog posts for category:', category);
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, hero_image, category, read_time')
          .eq('published', true)
          .eq('category', category)
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }
        console.log('Fetched posts:', data);
        setPosts(data || []);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [category]);

  if (loading) {
    return (
      <section className="py-16 md:py-24 px-4 bg-muted/20">
        <div 
          ref={blogReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            blogReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-muted-foreground">Loading insights...</p>
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 px-4 bg-muted/20">
      <div 
        ref={blogReveal.ref}
        className={`container mx-auto transition-all duration-1000 ${
          blogReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
            <p className="text-muted-foreground text-lg">
              {description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.slug}`}
                className="group"
              >
                <div className="bg-background rounded-2xl overflow-hidden shadow-lg h-full flex flex-col transition-all hover:shadow-2xl hover:-translate-y-1">
                  <img 
                    src={post.hero_image} 
                    alt={post.title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-accent text-sm font-semibold mb-2">{post.category}</div>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-accent transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 flex-1 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{post.read_time}</span>
                      <span className="text-accent font-semibold group-hover:underline">
                        Read more →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
